import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Button,
  Descriptions,
  Drawer,
  Input,
  List,
  Skeleton,
  Space,
  Tag,
  Tooltip,
  Typography,
  theme
} from "antd";
import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  ExclamationCircleTwoTone,
  CopyOutlined,
  ExportOutlined
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { getLead, setLeadStatus } from "./api";
import {
  SOURCE_SHORT,
  TRADE_LABELS,
  daysLeft,
  formatDate,
  formatDistance,
  formatEuro
} from "./constants";
import ScoreBadge from "./ScoreBadge";
import { Success } from "../../../commons/Messages/Messages";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import "./auftragssuche.css";

const REQUIREMENT_LABELS = {
  CERTIFICATE: "Certificate",
  REFERENCE: "References",
  INSURANCE: "Insurance",
  STAFFING: "Staff",
  OTHER: "Other"
};

const LeadDetailDrawer = ({ leadKey, open, onClose, onChanged, onPrepareOffer }) => {
  const { t, i18n } = useTranslation("dashboard");
  const lng = i18n.language;
  const [lead, setLead] = useState(null);
  const [notes, setNotes] = useState("");
  const isMobile = useIsMobile();
  const { token } = theme.useToken();

  const REASON_ICON = {
    OK: <CheckCircleTwoTone twoToneColor={token.colorSuccess} />,
    WARN: <ExclamationCircleTwoTone twoToneColor={token.colorWarning} />,
    FAIL: <CloseCircleTwoTone twoToneColor={token.colorError} />
  };

  const load = () =>
    getLead(leadKey)
      .then(res => {
        setLead(res.data);
        setNotes(res.data.lead?.notes || "");
      })
      .catch(err => {
        generalErrorAlert(err);
        onClose();
      });

  useEffect(() => {
    if (open && leadKey) {
      setLead(null);
      load();
    }
  }, [open, leadKey]);

  const changeStatus = async (status, extra = {}) => {
    try {
      await setLeadStatus(leadKey, { status, ...extra });
      await load();
      onChanged && onChanged();
    } catch (err) {
      generalErrorAlert(err);
    }
  };

  const saveNotes = () => {
    if (!lead || notes === (lead.lead?.notes || "")) return;
    changeStatus(lead.lead?.status === "DISMISSED" ? "DISMISSED" : "SAVED", { notes });
  };

  const copyMessage = () => {
    navigator.clipboard.writeText(lead.message.text).then(() => Success(t("Copied to clipboard.")));
  };

  const left = lead ? daysLeft(lead.submissionDeadline) : null;
  const status = lead?.lead?.status;

  return (
    <Drawer
      width={isMobile ? "100%" : 760}
      rootClassName="auftrag-lead-drawer"
      open={open}
      onClose={onClose}
      destroyOnClose
      title={lead ? lead.title : t("Loading...")}
      extra={
        lead && (
          <a href={lead.sourceUrl} target="_blank" rel="noopener noreferrer">
            <Tooltip title={isMobile ? t("Open original") : undefined}>
              <Button icon={<ExportOutlined />} aria-label={t("Open original")}>
                {!isMobile && t("Open original")}
              </Button>
            </Tooltip>
          </a>
        )
      }
      footer={
        lead && (
          <Space wrap>
            {lead.offer ? (
              <Link to="/user/offers">
                <Button type="primary">
                  {t("Offer")} {lead.offer.quotationNumber}
                </Button>
              </Link>
            ) : (
              <Button
                type="primary"
                onClick={() => onPrepareOffer(lead)}
                disabled={!lead.fit.eligible}
              >
                {t("Prepare offer")}
              </Button>
            )}
            {!lead.offer && status !== "SAVED" && (
              <Button onClick={() => changeStatus("SAVED")}>{t("Save")}</Button>
            )}
            {!lead.offer && status !== "DISMISSED" && (
              <Button onClick={() => changeStatus("DISMISSED")}>{t("Hide")}</Button>
            )}
            {!lead.offer && status && (
              <Button onClick={() => changeStatus(null)}>{t("Back to new matches")}</Button>
            )}
          </Space>
        )
      }
    >
      {!lead ? (
        <Skeleton active />
      ) : (
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          <div className="auftrag-lead-head">
            <ScoreBadge fit={lead.fit} size={isMobile ? 56 : 72} />
            <div className="auftrag-lead-head-text">
              <Space wrap size={[4, 4]}>
                <Tag color={lead.clientType === "PRIVATE" ? "purple" : "blue"}>
                  {lead.clientType === "PRIVATE" ? t("Private customer") : t("Public tender")}
                </Tag>
                <Tag>{SOURCE_SHORT[lead.sourceId]}</Tag>
                {lead.alsoPublishedAt.map(a => (
                  <Tag key={a.sourceId}>{SOURCE_SHORT[a.sourceId] || a.sourceId}</Tag>
                ))}
                {lead.trades.map(tr => (
                  <Tag key={tr} color="geekblue">
                    {t(TRADE_LABELS[tr] || tr)}
                  </Tag>
                ))}
                {lead.tradeVia === "TEXT" && <Tag color="gold">{t("Trade guessed from text")}</Tag>}
              </Space>
              <Typography.Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                {lead.fit.confidence === "LOW"
                  ? t(
                      "Little is known about this Auftrag yet, so the score is only a first indication."
                    )
                  : lead.fit.confidence === "MEDIUM"
                    ? t("The score is based on part of the relevant facts.")
                    : t("The score is based on most of the relevant facts.")}
              </Typography.Paragraph>
            </div>
          </div>

          <Alert
            type="info"
            showIcon
            message={lead.dataLevel.label}
            description={lead.dataLevel.nextStep}
          />

          <Descriptions
            size="small"
            column={1}
            bordered
            layout={isMobile ? "vertical" : "horizontal"}
            className="auftrag-lead-text"
          >
            <Descriptions.Item label={t("Client")}>
              {lead.buyerName || t("Private customer (anonymous until you reply on the portal)")}
            </Descriptions.Item>
            <Descriptions.Item label={t("Location")}>
              {lead.locationText || "–"}
              {lead.distanceKm !== null && ` · ${formatDistance(lead)}`}
              {lead.locationFromBuyer && (
                <Typography.Text type="secondary">
                  {" "}
                  ({t("seat of the contracting authority")})
                </Typography.Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t("Submission deadline")}>
              {formatDate(lead.submissionDeadline, lng, true)}
              {left !== null && (
                <Tag
                  color={left < 7 ? "red" : left < 14 ? "orange" : "green"}
                  style={{ marginInlineStart: 8 }}
                >
                  {t("{{count}} days left", { count: Math.max(left, 0) })}
                </Tag>
              )}
            </Descriptions.Item>
            {lead.questionDeadline && (
              <Descriptions.Item label={t("Questions until")}>
                {formatDate(lead.questionDeadline, lng, true)}
              </Descriptions.Item>
            )}
            {(lead.projectStart || lead.projectEnd) && (
              <Descriptions.Item label={t("Execution period")}>
                {formatDate(lead.projectStart, lng)} – {formatDate(lead.projectEnd, lng)}
              </Descriptions.Item>
            )}
            <Descriptions.Item label={t("Estimated value")}>
              {formatEuro(lead.estimatedValueEur, lng)}
            </Descriptions.Item>
            {lead.procedureType && (
              <Descriptions.Item label={t("Procedure")}>{lead.procedureType}</Descriptions.Item>
            )}
            {lead.bindingPeriodDays && (
              <Descriptions.Item label={t("Binding period")}>
                {t("{{count}} days", { count: lead.bindingPeriodDays })}
              </Descriptions.Item>
            )}
            {lead.documentsUrl && (
              <Descriptions.Item label={t("Tender documents")}>
                <a href={lead.documentsUrl} target="_blank" rel="noopener noreferrer">
                  {t("Open on the portal")}
                </a>
                {lead.documentsRestricted && (
                  <Tag color="red" style={{ marginInlineStart: 8 }}>
                    {t("Restricted access")}
                  </Tag>
                )}
              </Descriptions.Item>
            )}
          </Descriptions>

          <div>
            <Typography.Title level={5}>{t("Why this score")}</Typography.Title>
            <List
              size="small"
              dataSource={lead.fit.reasons}
              renderItem={r => (
                <List.Item>
                  <Space align="start" className="auftrag-lead-text">
                    {REASON_ICON[r.level]}
                    {r.text}
                  </Space>
                </List.Item>
              )}
            />
          </div>

          {lead.requirements.length > 0 && (
            <div>
              <Typography.Title level={5}>{t("Eligibility requirements")}</Typography.Title>
              <List
                size="small"
                dataSource={lead.requirements}
                renderItem={r => (
                  <List.Item>
                    <Space align="start" className="auftrag-lead-text">
                      <Tag>{t(REQUIREMENT_LABELS[r.kind] || r.kind)}</Tag>
                      <span>{r.text}</span>
                    </Space>
                  </List.Item>
                )}
              />
            </div>
          )}

          {lead.description && (
            <div>
              <Typography.Title level={5}>{t("Description")}</Typography.Title>
              <Typography.Paragraph
                ellipsis={{ rows: 8, expandable: true, symbol: t("more") }}
                className="auftrag-lead-text"
                style={{ whiteSpace: "pre-wrap" }}
              >
                {lead.description}
              </Typography.Paragraph>
            </div>
          )}

          <div>
            <Typography.Title level={5}>
              {lead.message.kind === "CUSTOMER_REPLY"
                ? t("Reply to the customer")
                : t("Questions to the contracting authority")}
            </Typography.Title>
            <Typography.Paragraph type="secondary">
              {lead.message.kind === "CUSTOMER_REPLY"
                ? t("Private requests go to several companies. Whoever answers first usually wins.")
                : t(
                    "Only asks about what the notice does not say. Send it through the tender portal."
                  )}
            </Typography.Paragraph>
            <Input.TextArea
              value={lead.message.text}
              autoSize={{ minRows: 6, maxRows: 16 }}
              readOnly
            />
            <Button icon={<CopyOutlined />} style={{ marginTop: 8 }} onClick={copyMessage}>
              {t("Copy")}
            </Button>
          </div>

          {lead.calculation && (
            <div>
              <Typography.Title level={5}>{t("Calculation basis (internal)")}</Typography.Title>
              <List
                size="small"
                dataSource={lead.calculation.derivations}
                renderItem={d => <List.Item>{d}</List.Item>}
              />
            </div>
          )}

          <div>
            <Typography.Title level={5}>{t("Notes")}</Typography.Title>
            <Input.TextArea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              onBlur={saveNotes}
              autoSize={{ minRows: 2, maxRows: 8 }}
              placeholder={t("Only visible to your company. Saving a note also saves the Auftrag.")}
            />
          </div>
        </Space>
      )}
    </Drawer>
  );
};

export default LeadDetailDrawer;
